import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import {
  Coins,
  Shield,
  Brain,
  TrendingUp,
  Target,
  Gamepad2,
  CheckCircle
} from 'lucide-react';

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Paper Trading: Practice Investing Without the Risk",
  "description": "Learn what paper trading is, why it works, and how Wallstreetle's coin system lets you practice stock analysis without risking real money.",
  "url": "https://wallstreetle.com/blog/paper-trading",
  "datePublished": "2026-03-20",
  "dateModified": "2026-03-20",
  "author": { "@type": "Organization", "name": "Wallstreetle", "url": "https://wallstreetle.com" },
  "publisher": { "@type": "Organization", "name": "Wallstreetle", "url": "https://wallstreetle.com" },
  "image": "https://wallstreetle.com/assets/share-link-image.png"
};

export default function PaperTradingPage() {
  return (
    <>
      <Helmet>
        <title>Wallstreetle | Paper Trading</title>
        <meta
          name="description"
          content="Learn what paper trading is and why practicing with simulated money builds real investing skills. Try Wallstreetle's free stock puzzles and paper trading system."
        />
        <meta
          name="keywords"
          content="paper trading, paper trade, simulated trading, virtual trading, practice investing, stock simulator, paper trading for beginners, demo trading, practice stock trading, learn trading"
        />
        <link rel="canonical" href="https://wallstreetle.com/blog/paper-trading" />

        <meta property="og:title" content="Wallstreetle | Paper Trading" />
        <meta property="og:description" content="Learn what paper trading is and how practicing with virtual money builds real investing confidence." />
        <meta property="og:url" content="https://wallstreetle.com/blog/paper-trading" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Wallstreetle | Paper Trading" />
        <meta property="twitter:description" content="Learn what paper trading is and how virtual money builds real investing confidence." />
        <meta property="twitter:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div className="max-w-5xl mx-auto py-12 px-4">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Coins className="w-4 h-4" /> Paper Trading
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-foreground mb-4">
            Paper Trading
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Practice investing with virtual money so you can build skills and confidence before putting real dollars on the line.
          </p>
        </div>

        {/* Section: What is Paper Trading */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">What is paper trading?</h2>
          <div className="prose dark:prose-invert max-w-none text-muted-foreground space-y-4">
            <p>
              Paper trading means buying and selling stocks with simulated money instead of real cash. The name comes from the old practice of tracking imaginary trades on paper. Today, paper trading platforms simulate real market conditions so you can learn without financial risk.
            </p>
            <p>
              Professional traders, hedge funds, and even experienced investors use paper trading to test new strategies before committing real capital. If the pros do it, beginners certainly should too.
            </p>
          </div>
        </section>

        {/* Section: Benefits */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-6">Benefits of practice before real money</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { title: 'Zero financial risk', desc: 'Make mistakes, learn from them, and pay nothing. Paper trading removes the fear that paralyzes beginners.', icon: Shield, color: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400' },
              { title: 'Build pattern recognition', desc: 'The more stocks you analyze, the faster you spot patterns in financial data: revenue trends, margin profiles, valuation ranges.', icon: Brain, color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' },
              { title: 'Develop discipline', desc: 'Paper trading teaches you to follow a process instead of acting on impulse. Good habits formed here carry over to real investing.', icon: Target, color: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400' },
              { title: 'Test strategies', desc: 'Value investing, growth investing, momentum. Try different approaches and see what fits your style before committing capital.', icon: TrendingUp, color: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400' },
              { title: 'Learn market mechanics', desc: 'Understand how prices move, what drives earnings, and how different sectors behave in various market conditions.', icon: Coins, color: 'bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400' },
              { title: 'Gain confidence', desc: 'When you finally invest real money, you\'ll have hundreds of data points from practice to inform your decisions.', icon: CheckCircle, color: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-600 dark:text-yellow-400' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.title} className="p-5 hover:shadow-lg transition-shadow">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${item.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Section: Wallstreetle's Coin System */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Wallstreetle's approach to paper trading</h2>
          <Card className="p-6 sm:p-8 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 border-purple-200 dark:border-purple-800">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/50 rounded-lg shrink-0">
                <Gamepad2 className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  Wallstreetle takes paper trading a step further with its <strong className="text-foreground">coin system</strong>. You earn coins by solving daily stock puzzles, then use those coins to take positions on stocks you believe in.
                </p>
                <p>
                  But unlike traditional paper trading platforms that just simulate buy/sell orders, Wallstreetle <strong className="text-foreground">trains your analytical skills first</strong>. Every puzzle teaches you to read price charts, evaluate financial metrics, and identify companies from their fundamentals. These are the exact skills you need to pick real stocks.
                </p>
                <p>
                  Think of it as a training program: the daily puzzles build your knowledge, and the coin system lets you apply it.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* Section: Building Confidence */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">From practice to real investing</h2>
          <div className="prose dark:prose-invert max-w-none text-muted-foreground space-y-4">
            <p>
              The transition from paper trading to real investing is one of the most important moments in an investor's journey. Studies show that traders who practice extensively before going live perform significantly better than those who jump in cold.
            </p>
            <p>
              With Wallstreetle, you're not just tracking hypothetical trades. You're building genuine familiarity with how companies look on paper. When you eventually open your brokerage app, the numbers on the screen won't be a foreign language anymore.
            </p>
          </div>
        </section>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-primary to-primary/80 text-white p-8 sm:p-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-3">
            Start practicing for free
          </h2>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            Solve daily stock puzzles, earn coins, and build the skills you need to invest with confidence.
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-primary font-semibold px-8 py-3 rounded-lg hover:bg-white/90 transition-colors"
          >
            Play Wallstreetle Free
          </Link>
        </Card>
      </div>
    </>
  );
}
